// Scripted pop-ups with known ground truth, sampled the way a pose model would
// report them: perspective-projected landmarks with jitter, dropped frames,
// occluded feet (low visibility, wrong positions) and left/right label swaps.

import type { Vec2 } from '../core/geometry';
import { toeSideSign } from '../core/mapping';
import { LM, NUM_LANDMARKS, LM_STRIDE, type PoseFrame } from '../core/pose';
import {
  blendBodies, bodyLandmarks, footPoints, pronePose, smoothstep, stancePose, swingFoot,
  type Body, type FootPoints,
} from './body';
import { projector, type SimWorld, type FootPose2D } from './world';

/** Offset from a target, cm: along (+ toward nose), across (+ toward toes), rot (deg, + toes toward nose). */
export interface Offset {
  along: number;
  across: number;
  rot?: number;
}

export interface Adjust extends Offset {
  foot: 'front' | 'rear';
  /** Start of the move, ms after the front foot lands. */
  afterMs: number;
  durMs?: number;
}

export interface PopupScript {
  reactionMs: number;
  pressMs: number;
  swingMs: number;
  frontLand: Offset;
  rearLand: Offset;
  adjustments: Adjust[];
  /** How long before the front foot the back foot plants, ms (default 60). */
  rearLeadMs?: number;
  noPopup?: boolean;
}

export interface Corruption {
  fps: number;
  /** Relative jitter of the frame interval (0..1). */
  fpsJitter: number;
  /** Landmark noise, σ in pixels. */
  jitterPx: number;
  /** Probability that a frame has no detection. */
  dropout: number;
  /** Feet the model can't see (ms relative to the cue). */
  occlusions: { foot: 'front' | 'rear'; from: number; to: number }[];
  /** Intervals where the model swaps left/right (ms relative to the cue). */
  labelSwaps: { from: number; to: number }[];
  seed: number;
}

/**
 * Timing follows a lab study of surfers' pop-ups (total 1.20 ± 0.19 s:
 * press-up ≈ 0.7 s, reach ≈ 0.5 s); reaction is time from the cue to moving.
 */
export const CLEAN_SCRIPT: PopupScript = {
  reactionMs: 320,
  pressMs: 650,
  swingMs: 480,
  frontLand: { along: 0, across: 0 },
  rearLand: { along: 0, across: 0 },
  adjustments: [],
};

export const GOOD_CAMERA: Corruption = {
  fps: 30,
  fpsJitter: 0.1,
  jitterPx: 1.2,
  dropout: 0.01,
  occlusions: [],
  labelSwaps: [],
  seed: 1,
};

// ---------------------------------------------------------------------------

export function rng(seed: number) {
  let a = seed >>> 0 || 1;
  const next = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const gauss = () => {
    const u = Math.max(1e-9, next());
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * next());
  };
  return { next, gauss };
}

export function offsetPose(w: SimWorld, target: FootPose2D, o: Offset): FootPose2D {
  const s = toeSideSign(w.stance);
  return {
    centre: { x: target.centre.x + o.across * s, y: target.centre.y + o.along },
    angle: target.angle + (((o.rot ?? 0) * Math.PI) / 180) * s,
  };
}

const footCentre = (f: FootPoints): Vec2 => ({ x: (f.heel.x + f.toe.x) / 2, y: (f.heel.y + f.toe.y) / 2 });

export interface PopupTruth {
  /** Times relative to the cue, ms. */
  onset: number;
  frontLanding: number;
  rearLanding: number;
  frontInitial: Vec2;
  rearInitial: Vec2;
  frontFinal: Vec2;
  rearFinal: Vec2;
  /** End of the last scripted move, relative to the cue. */
  settledFrom: number;
}

/** Body pose as a function of time since the cue. */
export function popupMotion(w: SimWorld, sc: PopupScript): { at: (tau: number) => Body; truth: PopupTruth } {
  const s = toeSideSign(w.stance);
  const prone = pronePose(w, 0);
  const front0 = s === 1 ? prone.lFoot : prone.rFoot;
  const rear0 = s === 1 ? prone.rFoot : prone.lFoot;
  const R = sc.reactionMs;
  const P = sc.pressMs;
  const S = sc.swingMs;
  const tFront = R + P + S;
  const tRear = tFront - (sc.rearLeadMs ?? 60);

  // Rest poses of each foot over time: landing, then each scripted adjustment.
  const frontPose0 = offsetPose(w, w.targets.front, sc.frontLand);
  const rearPose0 = offsetPose(w, w.targets.rear, sc.rearLand);
  type Move = { from: FootPoints; to: FootPoints; t0: number; t1: number; arc: number };
  const moves: Record<'front' | 'rear', Move[]> = { front: [], rear: [] };
  const current = { front: footPoints(frontPose0), rear: footPoints(rearPose0) };
  const poses = { front: frontPose0, rear: rearPose0 };
  let settledFrom = tFront;
  for (const adj of [...sc.adjustments].sort((a, b) => a.afterMs - b.afterMs)) {
    const p = poses[adj.foot];
    const next: FootPose2D = {
      centre: { x: p.centre.x + adj.across * s, y: p.centre.y + adj.along },
      angle: p.angle + (((adj.rot ?? 0) * Math.PI) / 180) * s,
    };
    const to = footPoints(next);
    const t0 = tFront + adj.afterMs;
    const t1 = t0 + (adj.durMs ?? 220);
    // A pivot rotates on the floor; a step lifts the foot.
    const arc = Math.hypot(adj.along, adj.across) > 1 ? 5 : 0;
    moves[adj.foot].push({ from: current[adj.foot], to, t0, t1, arc });
    current[adj.foot] = to;
    poses[adj.foot] = next;
    settledFrom = Math.max(settledFrom, t1);
  }
  const frontLanded = footPoints(frontPose0);
  const rearLanded = footPoints(rearPose0);

  const footAt = (role: 'front' | 'rear', tau: number): FootPoints => {
    const [start, landed, t0, t1, arc] =
      role === 'front' ? [front0, frontLanded, R + P, tFront, 24] : [rear0, rearLanded, R + P + 40, tRear, 8];
    if (tau <= t0) return start;
    if (tau < t1) return swingFoot(start, landed, (tau - t0) / (t1 - t0), arc);
    let f = landed;
    for (const m of moves[role]) {
      if (tau >= m.t1) f = m.to;
      else if (tau > m.t0) return swingFoot(m.from, m.to, (tau - m.t0) / (m.t1 - m.t0), m.arc);
    }
    return f;
  };

  const at = (tau: number): Body => {
    const breathe = Math.sin(tau / 700) * 0.6;
    if (sc.noPopup || tau < R) {
      const b = pronePose(w, 0.05 + breathe * 0.02);
      return b;
    }
    const lift = smoothstep((tau - R) / P);
    const blend = smoothstep((tau - R - 0.3 * P) / (0.7 * P + S + 120));
    const f = footAt('front', tau);
    const r = footAt('rear', tau);
    const sway = { x: Math.sin(tau / 900) * 0.8, y: Math.cos(tau / 1100) * 0.8 };
    const body = blendBodies(pronePose(w, lift), stancePose(w, f, r, 1, sway), blend);
    const [lFoot, rFoot] = s === 1 ? [f, r] : [r, f];
    return { ...body, lFoot, rFoot };
  };

  return {
    at,
    truth: {
      onset: R,
      frontLanding: tFront,
      rearLanding: tRear,
      frontInitial: footCentre(frontLanded),
      rearInitial: footCentre(rearLanded),
      frontFinal: footCentre(current.front),
      rearFinal: footCentre(current.rear),
      settledFrom,
    },
  };
}

// ---------------------------------------------------------------------------

const SWAP_PAIRS: [number, number][] = [
  [1, 4], [2, 5], [3, 6], [7, 8], [9, 10], [11, 12], [13, 14], [15, 16], [17, 18], [19, 20], [21, 22],
  [23, 24], [25, 26], [27, 28], [29, 30], [31, 32],
];
const FOOT_LM = {
  L: [LM.leftAnkle, LM.leftHeel, LM.leftToe],
  R: [LM.rightAnkle, LM.rightHeel, LM.rightToe],
};

export interface ObserveOptions {
  jitterPx: number;
  /** Per-label offsets for occluded feet (normalized image units); presence = occluded. */
  occluded: Partial<Record<'L' | 'R', { x: number; y: number }>>;
  swapLabels: boolean;
}

/** One frame of landmarks as a pose model would report this body. */
export function observeBody(w: SimWorld, body: Body, r: ReturnType<typeof rng>, o: ObserveOptions): Float32Array {
  const P = projector(w.camera);
  const sx = o.jitterPx / w.camera.width;
  const sy = o.jitterPx / w.camera.height;
  const pts = bodyLandmarks(body);
  const lm = new Float32Array(NUM_LANDMARKS * LM_STRIDE);
  for (let i = 0; i < NUM_LANDMARKS; i++) {
    const p = P(pts[i]);
    lm[i * 3] = p.x + r.gauss() * sx;
    lm[i * 3 + 1] = p.y + r.gauss() * sy;
    lm[i * 3 + 2] = Math.min(1, 0.93 + r.next() * 0.06);
  }
  for (const label of ['L', 'R'] as const) {
    const off = o.occluded[label];
    if (!off) continue;
    for (const i of FOOT_LM[label]) {
      lm[i * 3] += off.x;
      lm[i * 3 + 1] += off.y;
      lm[i * 3 + 2] = 0.1 + r.next() * 0.2;
    }
  }
  if (o.swapLabels) {
    for (const [a, b] of SWAP_PAIRS) {
      for (let k = 0; k < 3; k++) {
        const tmp = lm[a * 3 + k];
        lm[a * 3 + k] = lm[b * 3 + k];
        lm[b * 3 + k] = tmp;
      }
    }
  }
  return lm;
}

export const physicalLabel = (w: SimWorld, foot: 'front' | 'rear'): 'L' | 'R' =>
  (toeSideSign(w.stance) === 1) === (foot === 'front') ? 'L' : 'R';

/**
 * Samples `bodyAt(t)` (t = timeline ms) the way a pose model would report it.
 * `cueTime` anchors the corruption windows, which are relative to the cue.
 */
export function sampleFrames(
  w: SimWorld,
  bodyAt: (t: number) => Body | null,
  durationMs: number,
  c: Corruption,
  cueTime = 0,
): PoseFrame[] {
  const r = rng(c.seed);
  const frames: PoseFrame[] = [];
  // Persistent wrong guesses while a foot is occluded.
  const occOffset = c.occlusions.map(() => ({ x: (r.gauss() * 20) / w.camera.width, y: (r.gauss() * 20) / w.camera.height }));
  let t = 0;
  while (t <= durationMs) {
    const body = bodyAt(t);
    if (!body || r.next() < c.dropout) {
      frames.push({ t, lm: null });
    } else {
      const tau = t - cueTime;
      const occluded: ObserveOptions['occluded'] = {};
      c.occlusions.forEach((o, k) => {
        if (tau >= o.from && tau <= o.to) occluded[physicalLabel(w, o.foot)] = occOffset[k];
      });
      const swapLabels = c.labelSwaps.some((sw) => tau >= sw.from && tau <= sw.to);
      frames.push({ t, lm: observeBody(w, body, r, { jitterPx: c.jitterPx, occluded, swapLabels }) });
    }
    t += (1000 / c.fps) * (1 + (r.next() * 2 - 1) * c.fpsJitter);
  }
  return frames;
}

export interface SimulatedAttempt {
  frames: PoseFrame[];
  cueTime: number;
  truth: PopupTruth;
}

export function simulateAttempt(
  w: SimWorld,
  sc: PopupScript,
  c: Corruption = GOOD_CAMERA,
  opts: { preMs?: number; holdMs?: number } = {},
): SimulatedAttempt {
  const pre = opts.preMs ?? 1500;
  const { at, truth } = popupMotion(w, sc);
  const end = sc.noPopup ? pre + 5000 : pre + truth.settledFrom + (opts.holdMs ?? 1800);
  const frames = sampleFrames(w, (t) => at(t - pre), end, c, pre);
  return { frames, cueTime: pre, truth };
}

/** The rider holding a stance (for reference capture), with natural sway. */
export function simulateStill(
  w: SimWorld,
  opts: { durationMs?: number; front?: Offset; rear?: Offset; corruption?: Partial<Corruption> } = {},
): PoseFrame[] {
  const f = footPoints(offsetPose(w, w.targets.front, opts.front ?? { along: 0, across: 0 }));
  const r = footPoints(offsetPose(w, w.targets.rear, opts.rear ?? { along: 0, across: 0 }));
  const c: Corruption = { ...GOOD_CAMERA, ...opts.corruption };
  return sampleFrames(
    w,
    (t) => {
      const body = stancePose(w, f, r, 1, { x: Math.sin(t / 800) * 1.2, y: Math.cos(t / 1000) * 1.2 });
      return body;
    },
    opts.durationMs ?? 2000,
    c,
  );
}
